/**
 * AvatarStreamer, the 'main page' of the Pixel Streaming app.
 * -----------------------------------------------------------------------------
 * React component that orchestrates an end‑to‑end Pixel‑Streaming conversation.
 *
 * Responsibilities
 * ----------------
 * • Bootstraps session state from Redux or a join‑token and drives a four‑phase
 *   local state machine: "loading" → "permission" → "active" → "error".
 * • Establishes and maintains a WebSocket connection to the audio/puppet
 *   service with automatic reconnect and per‑status handling.
 * • Periodically polls the session API (every 5 s) to ensure the remote
 *   session is still alive and navigates away when it ends.
 * • Handles user‑interaction niceties: keyboard shortcuts, lobby/chrome
 *   toggles, click‑to‑play microphone permission flow, and screen expansion.
 * • Instantiates the PixelStreaming player via `useStreamerInstance`, provides
 *   audio controls, and surfaces host/guest UI overlays.
 *
 * The rest of the file is largely plain React hooks wiring: reducers drive the
 * local UI state, `useEffect` hooks manage side‑effects, and callbacks keep
 * everything connected without blocking the render thread.
 */

import { useReducer, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { WebSocketConnection, ConnectionStatus } from "../utils/WebSocketConnection";
import {
  createEnsureTalkableSessionMessage,
  createStringConsoleMessage,
  updateClientInfoState,
  updateRenderValues,
} from "../utils/protobuf_utils";
import { getDecryptedJwt } from "../utils/tokenEncryptionUtils";
import { SessionStatus, getSession, GetSessionError } from "../utils/sessionEndpoints";
import { HttpError } from "../api_middleware";
import {
  AudioMessage_DevicePermissions_BrowserPermission_Status as MicPerm,
  AudioMessage_DevicePermissions_BrowserInteractionClickState_Status as MicClick,
  AudioMessage_DisplayInfo_Orientation as Orientation,
  AudioMessage_ServerInfo_HealthStatus as HealthStatus,
} from "../generated/audio_message";
import { useIsMobilePortrait } from "../utils/graphicsUtils";

import {
  selectConversationId,
  selectUserIsHost,
  selectTokenMicEnabled,
  selectUserId,
  selectPixelStreamerURL,
  selectWebsocketUrl,
  selectConversationSessionSpec,
  selectSessionId,
  selectStreamToken,
  selectUserMode,
  setFeedbackData,
  UserMode,
  selectPreferredCodecOverride,
} from "../store/sessionSlice";
import { selectActivePixelStreamingExperience } from "../store/userSlice";
import { selectDevMode, setFlags } from "../store/flagsSlice";

import { useNavUtils } from "../utils/navUtils";
import { useSessionSetupFlow } from "../hooks/useSessionSetupFlows";
import { useMicrophonePermissions, PermissionOverlay } from "./MicrophonePermissions";
import { MicrophoneImpl } from "../utils/MicrophoneImpl";
import { useStreamerInstance } from "./useStreamerInstance";
import { DisplayMode } from "./PixelStreamingWrapper";
import { NativeBridge } from "../utils/parentAppBridge";

import DegradedHealthOverlay from "./utils/DegradedHealthOverlay";
import UserOverlay from "./UserOverlay";
import MicrophonePermissionUI from "./MicrophonePermissionUI";

import "../styles/components/AvatarStreamer.css";

// ───────────────────────── state machine ─────────────────────────

type Phase = "loading" | "permission" | "active" | "error";

interface LocalState {
  phase: Phase;
  error?: string;
  lobbyVisible: boolean;
  chromeHidden: boolean;
  displayMode: DisplayMode;
  streamerStarted: boolean;
  guestMicAllowed: boolean;
}

type Action =
  | { type: "PHASE"; phase: Phase; error?: string }
  | { type: "TOGGLE_LOBBY" }
  | { type: "TOGGLE_CHROME" }
  | { type: "SET_DISPLAY_MODE"; displayMode: DisplayMode }
  | { type: "STREAM_STARTED" }
  | { type: "SET_GUEST_MIC"; value: boolean };

const initialState: LocalState = {
  phase: "loading",
  lobbyVisible: true,
  chromeHidden: false,
  displayMode: "fit",
  streamerStarted: false,
  guestMicAllowed: false,
};

function reducer(s: LocalState, a: Action): LocalState {
  switch (a.type) {
    case "PHASE":
      return { ...s, phase: a.phase, error: a.error };
    case "TOGGLE_LOBBY":
      return { ...s, lobbyVisible: !s.lobbyVisible };
    case "TOGGLE_CHROME":
      return { ...s, chromeHidden: !s.chromeHidden };
    case "SET_DISPLAY_MODE":
      return { ...s, displayMode: a.displayMode };
    case "STREAM_STARTED":
      return { ...s, streamerStarted: true };
    case "SET_GUEST_MIC":
      return { ...s, guestMicAllowed: a.value };
    default:
      return s;
  }
}

// ───────────────────────── component ─────────────────────────

export default function AvatarStreamer() {
  // redux
  const conversationId = useSelector(selectConversationId);
  const userIsHost = useSelector(selectUserIsHost);
  const tokenMicEnabled = useSelector(selectTokenMicEnabled);
  const userId = useSelector(selectUserId);
  const pixelURL = useSelector(selectPixelStreamerURL);
  const wsURL = useSelector(selectWebsocketUrl);
  const spec = useSelector(selectConversationSessionSpec);
  const sessId = useSelector(selectSessionId);
  const streamToken = useSelector(selectStreamToken);
  const userMode = useSelector(selectUserMode);
  const activeExperience = useSelector(selectActivePixelStreamingExperience);
  const devMode = useSelector(selectDevMode);
  const isMobilePortrait = useIsMobilePortrait();

  // urls & params
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // utils
  const reduxDispatch = useDispatch();
  const { logoutReset, sessionEndNav } = useNavUtils();
  const { handleTokenSessionSetup } = useSessionSetupFlow();
  const [
    { needsPermission, showPermissionOverlay },
    { checkMicrophonePermission, requestPermissionAndStart },
  ] = useMicrophonePermissions();
  const preferredCodecOverride = useSelector(selectPreferredCodecOverride);

  // local state
  const [state, dispatch] = useReducer(reducer, initialState);
  const setPhase = (p: Phase, e?: string) => {
    console.log(`[AvatarStreamer] Setting phase to ${p}`);
    dispatch({ type: "PHASE", phase: p, error: e });
  };
  const [healthStatus, setHealthStatus] = useState<HealthStatus>(HealthStatus.HEALTHY);

  // The websocket connection
  const wsRef = useRef<WebSocketConnection | null>(null);

  // A ref to the container div for the avatar streamer, so we can get the dimensions
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // The timer used to validate sessions every 5 seconds
  const sessionValidationTimer = useRef<number | null>(null);

  const endSessionAndNavigateOut = useCallback(async () => {
    console.log("[AvatarStreamer] Session ended, navigating out.");
    wsRef.current?.disconnectAndStop();
    updateClientInfoState({ streamIsActive: false });
    const shouldAutoLogout = activeExperience?.logout_user_post_experience && !activeExperience.requests_feedback_post_experience;
    if (shouldAutoLogout) {
      await logoutReset();
    } else {
      if (activeExperience?.requests_feedback_post_experience !== true && conversationId !== undefined) {
        reduxDispatch(setFeedbackData({ conversationId: conversationId, botName: activeExperience?.bot_name }));
      }
    }
    NativeBridge.emitToNative({ type: "sessionEnded" });
    const shouldClearSession = !activeExperience?.requests_feedback_post_experience;
    sessionEndNav(shouldClearSession);
  }, [logoutReset, sessionEndNav, activeExperience, reduxDispatch, conversationId]);

  // ----------------------------
  // Component lifecycle logging

  useEffect(() => {
    console.log("[AvatarStreamer] Component mount");
    return () => {
      console.log("[AvatarStreamer] Component unmount — cleaning up WebSocket.");
      wsRef.current?.removeStatusListener("avatar");
      wsRef.current?.removeHealthStatusListener("avatar");
      wsRef.current?.disconnectAndStop();
      wsRef.current = null;
    };
  }, []);

  useEffect(() => {
    // Register for the native app kill session event
    const onKillSessionEvent = async (e: Event) => {
      const ce = e as CustomEvent;
      console.log("Got killSession event from native:", ce.detail);
      await endSessionAndNavigateOut();
    };
    window.addEventListener("killSession", onKillSessionEvent);
    return () => window.removeEventListener("killSession", onKillSessionEvent);
  }, [endSessionAndNavigateOut]);

  // Observer the size of the avatar container
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions(); // initial measure
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Set initial display mode based on device orientation
  useEffect(() => {
    const initialDisplayMode = isMobilePortrait ? "fill" : "fit";
    dispatch({ type: "SET_DISPLAY_MODE", displayMode: initialDisplayMode });
  }, [isMobilePortrait]);

  // ----------------------------
  // Websocket management

  const connectionStatusCallback = useCallback(
    (st: ConnectionStatus) => {
      console.log(`[AvatarStreamer] WebSocket status changed: ${st}`);
      if (st === "connected") {
        if (state.guestMicAllowed) {
          updateClientInfoState({
            microphonePermission: MicPerm.AUTHORIZED,
            pixelStreamingInteraction: MicClick.CLICKED,
          });
        }
      }
    },
    [state.guestMicAllowed]
  );

  const healthStatusCallback = useCallback((healthStatus: HealthStatus) => {
    setHealthStatus(healthStatus);
  }, []);

  // connect once eligibility met
  const readyForWS = wsURL && (state.phase === "permission" || state.phase === "active");
  //const readyForWS = wsURL && state.phase === "active";

  // The WebSocket creation effect.
  useEffect(() => {
    if (!readyForWS || wsRef.current) return;

    const token = getDecryptedJwt();
    console.log(
      "[AvatarStreamer] WebSocket effect starting",
      wsURL,
      "token:",
      token?.substring(0, 10)
    );

    // THIS IS WHERE WE INSTANTIATE THE WEBSOCKET CONNECTION!!!
    wsRef.current = WebSocketConnection.getInstance(wsURL, token);
    wsRef.current.addStatusListener("avatar", connectionStatusCallback);
    wsRef.current.addHealthStatusListener("avatar", healthStatusCallback);
    wsRef.current.setAutoReconnectEnabled(true);
    wsRef.current.connect();

    // NOTE! It gets cleaned up in the top-most non-dependent unmount effect.
  }, [readyForWS, wsURL, connectionStatusCallback, healthStatusCallback]);

  // ----------------------------
  // initial boot (redux vs token flow)

  useEffect(() => {
    console.log("[AvatarStreamer] Boot effect started.");

    setPhase("loading");

    // Do we have everything we need to start, put into redux from a previous screen?
    // If so, go right to the permission phase.
    const reduxReady =
      wsURL && conversationId && (pixelURL || params.get("bypass_pixel_streaming") === "True");
    if (reduxReady) {
      console.log("[AvatarStreamer] Have redux state ready, going to permission phase.");
      if (MicrophoneImpl.getInstance().isStarted()) {
        dispatch({ type: "SET_GUEST_MIC", value: true });
        setPhase("active");
      } else {
        dispatch({ type: "SET_GUEST_MIC", value: tokenMicEnabled || false });
        setPhase("permission");
      }
      return;
    }

    // No redux state - we need a join token to proceed.
    const joinToken = params.get("join_token");

    if (!joinToken) {
      // Not sure he we'd even get here...
      console.log("[AvatarStreamer] No token mode, going to permission phase.");
      dispatch({ type: "SET_GUEST_MIC", value: true });
      setPhase("permission");
      return;
    }

    // We have a join token, so we need to set up the session.
    console.log("[AvatarStreamer] Token mode, setting up session with join token");
    handleTokenSessionSetup(joinToken)
      .then((r) => {
        dispatch({ type: "SET_GUEST_MIC", value: r.tokenMicEnabled });
        setPhase("permission");
      })
      .catch((err) => {
        console.error("[AvatarStreamer] Error in token session setup: ", err);
        setPhase("error", err instanceof Error ? err.message : "setup failed");
      });

    return () => {
      console.log("[AvatarStreamer] Boot effect, cleanup is empty.");
    };
  }, [
    params,
    wsURL,
    conversationId,
    pixelURL,
    tokenMicEnabled,
    spec,
    handleTokenSessionSetup,
    logoutReset,
  ]);

  // ----------------------------
  // keyboard shortcuts

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const edit =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement ||
        document.activeElement?.hasAttribute("contenteditable");
      if (edit) return;

      // Single key shortcuts
      if (e.key === "q") dispatch({ type: "TOGGLE_LOBBY" });
      if (e.key === "w" && userIsHost) dispatch({ type: "TOGGLE_CHROME" });

      // Control key combinations
      if (e.ctrlKey && e.key === "k") {
        e.preventDefault(); // Prevent browser defaults
        if (wsRef.current && wsRef.current.isConnected()) {
          wsRef.current.sendMessage(createStringConsoleMessage("/reset"));
        }
      }
      if (e.ctrlKey && e.key === "j") {
        e.preventDefault(); // Prevent browser default (address bar focus)
        if (wsRef.current && wsRef.current.isConnected()) {
          wsRef.current.sendMessage(createEnsureTalkableSessionMessage());
        }
      }
      // Ctrl+Shift+D to toggle devMode
      if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        reduxDispatch(setFlags({ devMode: !devMode }));
        console.log(`[AvatarStreamer] DevMode toggled to: ${!devMode}`);
      }
    },
    [userIsHost, devMode, reduxDispatch]
  );

  useEffect(() => {
    console.log("[AvatarStreamer] Keyboard effect start.");
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      console.log("[AvatarStreamer] Keyboard effect cleanup.");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // ----------------------------
  // Validate the session every 5 seconds

  const checkSession = useCallback(async () => {
    // Checks whether session is still valid; if not, ends it and navigates out.
    if (!spec || !sessId) return;
    try {
      const s = await getSession(spec, sessId, false);
      if (s.status !== SessionStatus.InProgress) {
        await endSessionAndNavigateOut();
      }
    } catch (error) {
      // Only end the session for GetSessionError or HttpError
      if (error instanceof GetSessionError || error instanceof HttpError) {
        await endSessionAndNavigateOut();
      }
      // Otherwise, silently ignore the error (network timeout, etc.)
      // The user might have temporarily lost wifi...
    }
  }, [spec, sessId, endSessionAndNavigateOut]);

  useEffect(() => {
    if (state.phase === "loading") return;
    console.log("[AvatarStreamer] Session validation effect started.");
    if (sessionValidationTimer.current) clearInterval(sessionValidationTimer.current);
    sessionValidationTimer.current = window.setInterval(checkSession, 5_000);
    return () => {
      console.log("[AvatarStreamer] Session validation effect cleanup.");
      if (sessionValidationTimer.current) clearInterval(sessionValidationTimer.current);
    };
  }, [state.phase, checkSession]);

  // ----------------------------
  // click-to-play continue

  const clickContinue = useCallback(async () => {
    const perm = await checkMicrophonePermission();
    if (perm === "granted") {
      await MicrophoneImpl.getInstance().start();
      updateClientInfoState({
        microphonePermission: MicPerm.AUTHORIZED,
        pixelStreamingInteraction: MicClick.CLICKED,
      });
      dispatch({ type: "SET_GUEST_MIC", value: true });
      setPhase("active");
      return true;
    }
    const granted = await requestPermissionAndStart();
    updateClientInfoState({
      microphonePermission: granted ? MicPerm.AUTHORIZED : MicPerm.DENIED,
      pixelStreamingInteraction: MicClick.CLICKED,
    });
    if (granted) dispatch({ type: "SET_GUEST_MIC", value: true });
    if (granted) setPhase("active");
    return granted;
  }, [checkMicrophonePermission, requestPermissionAndStart]);

  // ----------------------------
  // streamer instance

  const handleStreamStarted = useCallback(() => {
    console.log("[AvatarStreamer] Stream started callback.");
    dispatch({ type: "STREAM_STARTED" });
    updateClientInfoState({ streamIsActive: true });

    // This will go away once streamIsActive is working with the server.
    if (userMode === UserMode.SingleUser) {
      console.log("[AvatarStreamer] sending ensure talkable session message.");
      wsRef.current?.sendMessage(createEnsureTalkableSessionMessage());
    }
  }, [dispatch, userMode]);

  const setDisplayMode = useCallback(
    (displayMode: DisplayMode) => {
      dispatch({ type: "SET_DISPLAY_MODE", displayMode: displayMode });
      const dims = (displayMode === "reres" || displayMode === "fill") ? dimensions : { width: 1920, height: 1080 };
      // For now: always landscape. I'm not sure we should EVER send portrait, even in portrait mode.
      updateRenderValues(Orientation.LANDSCAPE, dims.width, dims.height);
    },
    [dispatch, dimensions]
  );

  const { component: pixelStreamer, audioControls, codecControls } = useStreamerInstance({
    isActive: state.phase === "active",
    displayMode: state.displayMode,
    sessionSpec: spec,
    sessionId: sessId,
    streamToken: streamToken,
    pixelStreamerURL: pixelURL,
    needsPermission,
    shouldClickToPlayContinue: clickContinue,
    onStreamStarted: handleStreamStarted,
  });

  useEffect(() => {
    if (!preferredCodecOverride) return;
    codecControls.setPreferredCodec(preferredCodecOverride);
  }, [preferredCodecOverride]);


  // ----------------------------
  // render

  return (
    <div ref={containerRef} className="avatar-container">
      {state.phase === "loading" && <div className="conversation-not-available">Loading…</div>}
      {state.phase === "error" && <div className="conversation-not-available">{state.error}</div>}
      {state.phase === "active" && (
        <>
          {pixelStreamer}
          <UserOverlay
            connection={wsRef.current}
            displayMode={state.displayMode}
            setDisplayMode={setDisplayMode}
            onAudioSpeakerChange={audioControls.changeDevice}
            onAudioSpeakerMuteChange={audioControls.mute}
            audioSpeakerMuteGetter={audioControls.isMuted}
            onAudioSpeakerVolumeChange={audioControls.setVolume}
            audioSpeakerVolumeGetter={audioControls.getVolume}
          />
        </>
      )}
      {/* {state.streamerStarted || <LoadingSessionComponent />} */}
      {/* <FadeOut in={!state.streamerStarted} duration={2_000}>
        <LoadingSessionComponent />
      </FadeOut> */}
      {/* <FadeOut in={!state.streamerStarted} duration={2_000}>
        <video className="background-video" autoPlay loop muted>
          <source src={lobbyBackgroundVideo} type="video/mp4" />
        </video>
      </FadeOut> */}
      {state.phase === "permission" && (
        <MicrophonePermissionUI
          accessRequired={needsPermission}
          onMicrophoneButtonClick={clickContinue}
        />
      )}
      {showPermissionOverlay && <PermissionOverlay />}

      {healthStatus === HealthStatus.SEVERELY_DEGRADED && (
        <DegradedHealthOverlay />
      )}
    </div>
  );
}
